import type { DaedalusGameAdapter, StandInArt } from "daedalus-core";
import { CHARACTER_MANIFEST, STAND_IN_CHARACTER, odysseyStandInArt, validateSheet } from "odyssey-shared";
import { say } from "../words.ts";

/**
 * The stand-in's art on the editor page (ADR-071 decision 1): a player standing in the fallback look, which the
 * build bakes to a one-layer character sheet (ADR-094 decision 33), read from the character manifest the game reads,
 * whose file names are content hashes, so the page composes it over the adapter rather than the adapter carrying it.
 * A manifest that lists no baked sheet, as one built without the look fixtures does, leaves the stand-in a box. The
 * art is a view aid: a manifest the page cannot use leaves it out with one warning and never stops the editor. The
 * manifest is read once (`readCharacterManifest`), and the creature picker draws its looks from the same read
 * (`creature-lookup.ts`).
 */

/**
 * The character manifest as read: its characters by name, each sheet still unchecked, or why it could not be
 * used.
 */
export type CharacterManifestRead = { readonly ok: true; readonly characters: Readonly<Record<string, unknown>> } | { readonly ok: false; readonly reason: string };

/**
 * Reads the character manifest. A request not answered, a status other than OK and a body that is not JSON are
 * each a reason, never a throw; a body with no characters object reads as a manifest listing no character, so the
 * stand-in says it lists no player to draw and every creature entry is drawn without art.
 *
 * @param base URL prefix the manifest and its `assets/...` paths sit under.
 * @param fetcher The fetch to use.
 * @returns The characters, or the reason, which starts with the manifest's URL.
 */
export async function readCharacterManifest(base: string, fetcher: typeof fetch = fetch): Promise<CharacterManifestRead> {
  const url = `${base}/${CHARACTER_MANIFEST}`;
  let response: Response;
  try {
    response = await fetcher(url);
  } catch (error) {
    return { ok: false, reason: say("editorNotice.standIn.unanswered", { url, reason: error instanceof Error ? error.message : String(error) }) };
  }
  if (!response.ok) {
    return { ok: false, reason: say("editorNotice.standIn.responded", { url, status: String(response.status) }) };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(await response.text());
  } catch {
    return { ok: false, reason: say("editorNotice.standIn.notJson", { url }) };
  }
  const characters = typeof parsed === "object" && parsed !== null ? (parsed as { characters?: unknown }).characters : undefined;
  return typeof characters === "object" && characters !== null ? { ok: true, characters: characters as Record<string, unknown> } : { ok: true, characters: {} };
}

/**
 * The stand-in's art from a manifest read. Any refusal leaves the art out and warns once: the manifest unusable,
 * a sheet `validateSheet` refuses, or a manifest that lists no baked fallback look.
 *
 * @param read The manifest as read.
 * @param base URL prefix the manifest's `assets/...` paths sit under.
 * @param warn Told once why the art was left out.
 * @returns The art, or null when it was left out.
 */
export function standInArtFrom(read: CharacterManifestRead, base: string, warn: (message: string) => void): StandInArt | null {
  const url = `${base}/${CHARACTER_MANIFEST}`;
  if (!read.ok) {
    warn(`the player stand-in draws as a box: ${read.reason}`);
    return null;
  }
  const sheet = read.characters[STAND_IN_CHARACTER];
  if (sheet === undefined) {
    warn(`the player stand-in draws as a box: ${url} lists no ${STAND_IN_CHARACTER}`);
    return null;
  }
  try {
    return odysseyStandInArt(validateSheet(sheet), base);
  } catch (error) {
    warn(`the player stand-in draws as a box: ${url} ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

/**
 * Reads the stand-in's art from the character manifest, as `readCharacterManifest` then `standInArtFrom` do.
 *
 * @param base URL prefix the manifest and its `assets/...` paths sit under.
 * @param warn Told once why the art was left out.
 * @param fetcher The fetch to use.
 * @returns The art, or null when it was left out.
 */
export async function loadStandInArt(base: string, warn: (message: string) => void, fetcher: typeof fetch = fetch): Promise<StandInArt | null> {
  return standInArtFrom(await readCharacterManifest(base, fetcher), base, warn);
}

/**
 * The adapter with the stand-in's art added, composed over the adapter the editor edits with, so a served
 * contract's types are kept. An adapter that registers no stand-in, or art that was left out, is returned
 * as it is.
 *
 * @param adapter The adapter `editorAdapter` returned.
 * @param art The art, or null.
 * @returns The adapter to hand to the store.
 */
export function withStandInArt(adapter: DaedalusGameAdapter, art: StandInArt | null): DaedalusGameAdapter {
  if (adapter.standIn === undefined || art === null) {
    return adapter;
  }
  return { ...adapter, standIn: { ...adapter.standIn, art } };
}
